/**
 * Maps the `projecthead/school-stat/*` payloads onto the props the blocks in
 * shared/components/schoolAnalytics expect.
 *
 * The components stay headless: everything shape-specific to the API lives
 * here, so a payload change touches one file.
 */

import type {
  BookRow,
  Delta,
  FunnelStage,
  SchoolRankRow,
  SchoolRef,
  Slice,
  StatusBand,
  Tone,
  TrendLegendItem,
  TrendSeries,
} from "shared/components/schoolAnalytics";
import { BAND_STYLE, CHART_COLORS, FUNNEL_COLORS, fmt } from "./data";

/* ------------------------------------------------------------------ *
 * Payload types                                                       *
 * ------------------------------------------------------------------ */

/** Every trended figure comes back in this shape. */
export interface ApiTrend {
  direction: "up" | "down" | "neutral";
  percentage: number;
}

export interface ApiFigure {
  value: number;
  trend?: ApiTrend;
  percentage?: number;
}

export interface OverallStat {
  total_schools: number;
  total_campuses: number;
  student_seats: number;
  teacher_seats: number;
  total_classrooms: number;
}

export interface DashboardStats {
  demo_accounts: ApiFigure;
  live_premium_accounts: ApiFigure;
  expired_premium_accounts: ApiFigure;
  expiring_premium_accounts: ApiFigure;
  renewed_premium_accounts: ApiFigure;
  test_accounts: ApiFigure;
}

interface HealthBucket {
  count: number;
  schools: {
    school_id: number;
    name: string;
    /** 1 = head office with campuses under it, 0 = a single standalone school. */
    is_multi_campus?: number;
  }[];
}

export interface HealthStat {
  healthy: HealthBucket;
  slipping: HealthBucket;
  dead: HealthBucket;
}

export interface SeatFunnel {
  seats_purchased: number;
  enrolled_students: ApiFigure;
  page_openers: ApiFigure;
  three_plus_book_readers: ApiFigure;
}

export interface RegularReader {
  enrolled_students: number;
  regular_readers: ApiFigure;
}

export interface ReadingLevel {
  enrolled_students: number;
  moved_up: ApiFigure;
  same_level: number;
}

export interface NobodyAssigning {
  count: number;
  schools: {
    school_id: number;
    name: string;
    campus_id: number | null;
    campus_name: string | null;
  }[];
}

interface MixBucket {
  total_books_read: number;
  items: {
    id?: number;
    name?: string;
    source?: string;
    books_read: number;
    percentage: number;
  }[];
}

export interface ReadingMix {
  genres?: MixBucket;
  languages?: MixBucket;
  age_groups?: MixBucket;
  sources?: MixBucket;
  publishers?: MixBucket;
}

export interface TopBooks {
  books: {
    book_id: number;
    name: string;
    author: string | null;
    cover_photo: string | null;
    language: string | null;
    age_group: string | null;
    read_count: number;
    completed_count: number;
    percentage: number;
  }[];
}

export interface GrowthTrend {
  type: string;
  granularity: string;
  total: number;
  points: { label: string; from: string; to: string; value: number }[];
}

export interface ReaderSchools {
  schools: {
    school_id: number;
    head_office: {
      name: string | null;
      address: string | null;
      email: string | null;
      contact: string | null;
    };
    enrolled_students: number;
    total_seats: number;
    lead_time: number;
    /** active | expired | paused */
    status?: string;
    /** The school pays its plan in installments. */
    has_installment?: boolean;
    health_band: string;
    engagement_time: number;
    regular_readers: number;
    reading_trend?: ApiTrend;
    /** 1 = head office with campuses under it, 0 = a single standalone school.
     *  Decides which analytics dashboard the school's detail screen renders. */
    is_multi_campus?: number;
  }[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

/* ------------------------------------------------------------------ *
 * Shared helpers                                                      *
 * ------------------------------------------------------------------ */

/** Turns the API's direction/percentage pair into the UI's delta chip. */
export const trendDelta = (
  trend?: ApiTrend,
  against?: string,
  /** Figures that improve when they fall, e.g. expired accounts. */
  invert = false
): Delta | undefined => {
  if (!trend) return undefined;
  const percentage = Number(trend.percentage ?? 0);

  let tone: Tone = "neutral";
  if (trend.direction === "up") tone = invert ? "danger" : "success";
  if (trend.direction === "down") tone = invert ? "success" : "danger";

  return {
    value: `${percentage >= 0 ? "+" : ""}${percentage}%`,
    tone,
    against,
  };
};

const share = (part: number, whole: number) =>
  whole > 0 ? (part / whole) * 100 : 0;

const initialsOf = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/** "teacher_assigned" becomes "Teacher assigned". */
const humanize = (value: string) =>
  value.replace(/_/g, " ").replace(/^./, (first) => first.toUpperCase());

/* ------------------------------------------------------------------ *
 * Block adapters                                                      *
 * ------------------------------------------------------------------ */

export const toHeaderStats = (overall?: OverallStat) => [
  { value: fmt(overall?.total_schools ?? 0), label: "accounts" },
  { value: fmt(overall?.student_seats ?? 0), label: "seats" },
  { value: fmt(overall?.total_campuses ?? 0), label: "campuses" },
];

/** The six account counts, in the order the design lists them. */
export const ACCOUNT_METRIC_DEFS: {
  key: keyof DashboardStats;
  label: string;
  unit: string;
  /** Falling is the good direction for this one. */
  invert?: boolean;
}[] = [
  { key: "demo_accounts", label: "Demo accounts", unit: "trials" },
  { key: "live_premium_accounts", label: "Premium accounts", unit: "active" },
  {
    key: "expired_premium_accounts",
    label: "Expired premium",
    unit: "lapsed",
    invert: true,
  },
  {
    key: "expiring_premium_accounts",
    label: "Expiring premium",
    unit: "renewals due",
    invert: true,
  },
  {
    key: "renewed_premium_accounts",
    label: "Repurchased accounts",
    unit: "renewed",
  },
  {
    key: "test_accounts",
    label: "Test accounts",
    unit: "internal · excluded from all figures",
  },
];

export const toBands = (health?: HealthStat): StatusBand[] => {
  if (!health) return [];

  const buckets: { key: keyof HealthStat; label: string }[] = [
    { key: "healthy", label: "Healthy" },
    { key: "slipping", label: "Slipping" },
    { key: "dead", label: "Dead" },
  ];

  const total = buckets.reduce(
    (sum, bucket) => sum + (health[bucket.key]?.count ?? 0),
    0
  );

  return buckets.map(({ key, label }) => {
    const bucket = health[key];
    const count = bucket?.count ?? 0;

    return {
      key: label,
      label,
      color: BAND_STYLE[label as keyof typeof BAND_STYLE].color,
      count,
      share: `${Math.round(share(count, total))}%`,
      schools: (bucket?.schools ?? []).map((school) => ({
        id: school.school_id,
        name: school.name,
      })),
    };
  });
};

/** Multi-campus schools repeat per campus; the chip list shows each one once. */
export const toSilentSchools = (silent?: NobodyAssigning): SchoolRef[] => {
  const seen = new Set<string>();

  return (silent?.schools ?? []).reduce<SchoolRef[]>((rows, school) => {
    const key = `${school.school_id}-${school.campus_id ?? "head"}`;
    if (seen.has(key)) return rows;
    seen.add(key);

    rows.push({
      id: school.school_id,
      name: school.campus_name
        ? `${school.name} · ${school.campus_name}`
        : school.name,
    });
    return rows;
  }, []);
};

export const toFunnelStages = (
  funnel: SeatFunnel | undefined,
  against: string
): FunnelStage[] => {
  if (!funnel) return [];

  const seats = funnel.seats_purchased ?? 0;

  const raw: { key: string; label: string; figure?: ApiFigure }[] = [
    { key: "seats", label: "Seats purchased", figure: { value: seats } },
    {
      key: "enrolled",
      label: "Student accounts created",
      figure: funnel.enrolled_students,
    },
    { key: "openers", label: "Read at least once", figure: funnel.page_openers },
    {
      key: "threePlus",
      label: "Finished 3+ books",
      figure: funnel.three_plus_book_readers,
    },
  ];

  return raw.map((stage, index) => {
    const value = stage.figure?.value ?? 0;
    const previous = raw[index - 1]?.figure?.value ?? 0;

    return {
      key: stage.key,
      stage: index + 1,
      label: stage.label,
      value,
      share: share(value, seats),
      color: FUNNEL_COLORS[index],
      delta: index === 0 ? undefined : trendDelta(stage.figure?.trend, against),
      drop: index === 0 ? undefined : share(previous - value, previous),
    };
  });
};

/** The five dimensions the reading-mix endpoint returns. */
export const MIX_DIMENSIONS = [
  { key: "genres", label: "Genre" },
  { key: "languages", label: "Language" },
  { key: "age_groups", label: "Age group" },
  { key: "sources", label: "Source" },
  { key: "publishers", label: "Publisher" },
];

export const toMixSlices = (
  mix: ReadingMix | undefined,
  dimension: string
): Slice[] => {
  const bucket = mix?.[dimension as keyof ReadingMix];

  return (bucket?.items ?? []).map((item, index) => ({
    label: item.name ?? humanize(item.source ?? "Unknown"),
    share: item.percentage ?? 0,
    color: CHART_COLORS[index % CHART_COLORS.length],
    count: item.books_read,
  }));
};

/* ------------------------------------------------------------------ *
 * Assignment pipeline                                                 *
 * ------------------------------------------------------------------ */

/** `school-stat/assignment-count`: assigned books split by status. */
export interface AssignmentCount {
  date_range?: { from: string; to: string; days: number };
  total_assignments: number;
  not_started_books: number;
  in_progress_books: number;
  completed_books: number;
  overdue_books: number;
}

const PIPELINE_STAGE_DEFS: [string, keyof AssignmentCount, string][] = [
  ["Not started", "not_started_books", "#adb5bd"],
  ["In progress", "in_progress_books", "#f4b740"],
  ["Completed", "completed_books", "#2e9e8f"],
  ["Overdue", "overdue_books", "#ef437b"],
];

/** Not started / In progress / Completed / Overdue, sized against the total. */
export const toPipelineSlices = (data?: AssignmentCount): Slice[] => {
  if (!data) return [];

  const stages = PIPELINE_STAGE_DEFS.map(([label, key, color]) => ({
    label,
    color,
    count: Number(data[key]) || 0,
  }));

  // The four buckets add up to total_assignments; keep the sum as the
  // denominator so the legend percentages match the count in the centre.
  const total =
    stages.reduce((sum, stage) => sum + stage.count, 0) ||
    Number(data.total_assignments) ||
    0;
  if (!total) return [];

  return stages.map((stage) => ({
    ...stage,
    share: (stage.count / total) * 100,
  }));
};

/** "x% done" under the donut centre, driven by the completed stage. */
export const toPipelineCaption = (data?: AssignmentCount): string => {
  const slices = toPipelineSlices(data);
  const done = slices.find((slice) => slice.label === "Completed");
  return done ? `${Math.round(done.share)}% done` : "";
};

export const BOOK_SORTS = [
  { key: "by_read", label: "By reads" },
  { key: "by_complete", label: "By finishes" },
];

export const toBookRows = (top?: TopBooks): BookRow[] =>
  (top?.books ?? []).map((book, index) => ({
    id: book.book_id,
    rank: index + 1,
    title: book.name,
    author: book.author || "Unknown author",
    language: book.language || "—",
    age: book.age_group || "—",
    cover: CHART_COLORS[index % CHART_COLORS.length],
    coverPhoto: book.cover_photo ?? undefined,
    initials: initialsOf(book.name),
    reads: fmt(book.read_count ?? 0),
    finished: fmt(book.completed_count ?? 0),
    finishPct: `${Math.round(book.percentage ?? 0)}%`,
  }));

export const TREND_METRICS = [
  { key: "book_read", label: "Books Read" },
  { key: "cumulative_reading", label: "Cumulative Reading" },
  { key: "engagement", label: "Engagement" },
];

const TREND_COLOR: Record<string, string> = {
  book_read: "#9a469b",
  cumulative_reading: "#ef437b",
  engagement: "#2e9e8f",
};

/** Engagement comes back in hours; the other two are plain book counts. */
export const toTrend = (
  trend: GrowthTrend | undefined,
  metric: string
): { series: TrendSeries[]; legend: TrendLegendItem[]; labels: string[] } => {
  const points = trend?.points ?? [];
  const label = TREND_METRICS.find((item) => item.key === metric)?.label ?? "";
  const hours = metric === "engagement";
  const color = TREND_COLOR[metric] ?? TREND_COLOR.book_read;

  return {
    labels: points.map((point) => point.label),
    series: [
      {
        key: metric,
        label,
        color,
        points: points.map((point) => point.value ?? 0),
        suffix: hours ? "h" : undefined,
      },
    ],
    legend: [
      {
        key: metric,
        label,
        color,
        total: hours
          ? `${Number(trend?.total ?? 0).toFixed(1)}h`
          : fmt(trend?.total ?? 0),
      },
    ],
  };
};

export const ACCOUNT_FILTERS = [
  { key: "all", label: "All accounts" },
  { key: "demo", label: "Demo" },
  { key: "premium", label: "Premium" },
  { key: "expired", label: "Expired" },
  { key: "test", label: "Test" },
];

/** Keys match the `status` values `school-stat/readerSchool` accepts. */
export const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "expired", label: "Expired" },
  { key: "paused", label: "Paused" },
];

/** "dead" becomes "Dead", matching the band badge. */
const bandLabel = (band?: string) =>
  band ? band.charAt(0).toUpperCase() + band.slice(1).toLowerCase() : "—";

export const toRankRows = (
  reader: ReaderSchools | undefined,
  against: string
): SchoolRankRow[] => {
  const pagination = reader?.pagination;
  const offset =
    ((pagination?.current_page ?? 1) - 1) * (pagination?.per_page ?? 10);

  return (reader?.schools ?? []).map((school, index) => {
    const office = school.head_office ?? ({} as ReaderSchools["schools"][0]["head_office"]);
    const name = office.name || `School #${school.school_id}`;
    const minutes = Math.round((school.engagement_time ?? 0) * 60);

    return {
      id: school.school_id,
      rank: offset + index + 1,
      name,
      campus: office.address || "—",
      city: "",
      band: bandLabel(school.health_band),
      status: bandLabel(school.status),
      hasInstallment: Boolean(school.has_installment),
      minutes: `${fmt(minutes)} min`,
      delta: trendDelta(school.reading_trend, against),
      regular: school.regular_readers ?? 0,
      regularPct: `${Math.round(
        share(school.regular_readers ?? 0, school.enrolled_students ?? 0)
      )}%`,
      seatsUsed: school.enrolled_students ?? 0,
      seats: school.total_seats ?? 0,
      lead: school.lead_time ?? 0,
      contact: name,
      role: office.email || "—",
      phone: office.contact || "—",
      initials: initialsOf(name),
    };
  });
};
